/**
 * THE WORKSPACE'S CURRENCY, ASKED.
 *
 * Every money column is named `*_inr`, but it holds the workspace's own
 * currency. Until 28 Sep 2026 nobody was ever asked which one that is: it was
 * guessed from orders, else a Settings select whose INR default was saved on
 * every save, else INR. A US lead-gen workspace read its deal values as rupees.
 *
 * Three places ask, all through the one write (POST /api/settings/currency,
 * services/workspaceCurrency.choose):
 *   CurrencyConfirmBanner  top of every page, for an admin, until answered
 *   CurrencySettingsCard   Settings > General: the choice, where it came from,
 *                          and orders that arrive in another currency
 *   /welcome               the last question a new workspace answers
 *
 * Always an explicit Save: picking in the list only stages the choice.
 * Changing it relabels existing numbers; it never converts them, and every
 * surface that saves says so before it does.
 */
import { useEffect, useState } from 'react';
import { Coins, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useChooseCurrency, useWorkspaceCurrency } from '@/hooks/use-workspace-currency';
import { useOrgSettings } from '@/hooks/use-settings';
import { getUserRole } from '@/lib/auth';
import { confirmAction } from '@/lib/confirm-action';
import { cn, fmtZoned } from '@/lib/utils';

const FALLBACK_OFFERED = [
  { code: 'INR', name: 'Indian rupee' }, { code: 'USD', name: 'US dollar' }, { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British pound' }, { code: 'AED', name: 'UAE dirham' },
];

const SOURCE_WORDS = {
  confirmed: 'Chosen by your team',
  store: 'Detected from your store',
  orders: 'Detected from your orders',
  setting: 'From your settings, not yet confirmed',
  zone: 'Detected from your time zone',
  phones: "Detected from your leads' phone numbers",
  browser: 'Detected from where you are',
  default: 'Not chosen yet',
};

const n = (v) => Number(v || 0).toLocaleString('en-US');

/**
 * The list. Controlled; saves nothing. The suggestion arrives preselected and
 * its reason is said in words beside it, so the option labels stay short
 * enough to read in the closed box.
 */
export function CurrencySelect({ value, onChange, offered, suggested, disabled, className, id }) {
  const list = (offered && offered.length ? offered : FALLBACK_OFFERED).slice();
  // A chosen or suggested code the short list does not carry is still shown.
  for (const code of [value, suggested]) {
    if (code && !list.some((o) => o.code === code)) list.unshift({ code, name: code });
  }
  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className={cn('h-9', className)}><SelectValue placeholder="Pick a currency" /></SelectTrigger>
      <SelectContent>
        {list.map((o) => (
          <SelectItem key={o.code} value={o.code}>
            {o.code} - {o.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** What choosing `next` does, in the words the confirm dialog shows. */
function relabelWords(current, next) {
  return `Every amount already stored (deal values, orders, revenue) is shown in ${next} from now on. `
    + `The numbers stay exactly as they are: ${current} 1,000 becomes ${next} 1,000. Nothing is converted. `
    + `Values sent to ad platforms with a lead conversion are sent in ${next} too.`;
}

/**
 * Stages a choice and saves it on an explicit click. `onSaved` runs after the
 * server has it. Used by the banner and the Settings card.
 */
function useStagedChoice(data, { allowDefault = false, prefer = null } = {}) {
  const choose = useChooseCurrency();
  const { toast } = useToast();
  const [draft, setDraft] = useState(null);
  useEffect(() => { setDraft(null); }, [data?.currency, data?.confirmed]);
  // Nothing detected means nothing to suggest: the list starts empty so the
  // admin picks, rather than a rupee default being confirmed by reflex.
  const guessed = data?.suggestion && data.suggestion.source !== 'default' ? data.suggestion.currency : null;
  // Settings shows what is on screen now (allowDefault); the banner does not.
  // `prefer`: the store's own currency, offered first when a confirmed choice
  // is not it (CurrencyConfirmBanner's mismatch).
  const initial = data ? (prefer || (data.confirmed ? data.currency : guessed || (allowDefault ? data.currency : null))) : null;
  const value = draft || initial;
  const save = async () => {
    if (!value || !data) return false;
    // Changing what the workspace already shows relabels every number: say so
    // before it happens. Confirming the currency already on screen changes
    // nothing a reader sees, so it needs no second question.
    if (value !== data.currency) {
      const ok = await confirmAction({
        title: `Show this workspace in ${value}?`,
        description: relabelWords(data.currency, value),
        confirmLabel: `Use ${value}`,
      });
      if (!ok) return false;
    }
    try {
      await choose.mutateAsync(value);
      toast({ title: `Currency saved: ${value}`, variant: 'success' });
      setDraft(null);
      return true;
    } catch (err) {
      toast({ title: 'Could not save the currency', description: err.response?.data?.error || err.message, variant: 'destructive' });
      return false;
    }
  };
  return { value, setDraft, dirty: !!draft && draft !== initial, save, saving: choose.isPending, initial };
}

/**
 * The one-time question, as the top banner. Admins only (the choice relabels
 * the whole workspace); shown until someone answers it. No dismiss: an
 * unanswered currency is a wrong label on every amount.
 */
export function CurrencyConfirmBanner() {
  const isAdmin = String(getUserRole() || '').toLowerCase() === 'admin';
  const { data } = useWorkspaceCurrency({ enabled: isAdmin });
  const s = (data && data.suggestion) || {};
  // A confirmed currency that is not the store's own. Orders are stored in
  // the currency the store sends, so every amount was shown under the wrong
  // name: Trendia's dollars read as rupees on Command Center (11 Oct 2026).
  const mismatch = !!(data && data.confirmed && s.source === 'store' && s.currency && s.currency !== data.currency);
  const staged = useStagedChoice(data, { prefer: mismatch ? s.currency : null });
  if (!isAdmin || !data || (data.confirmed && !mismatch)) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b card-attention px-4 py-2 text-[13px] text-amber-900 dark:text-amber-200"
      data-testid="currency-confirm-banner">
      <Coins className="size-4 shrink-0" />
      {/* At least 16rem of reading width: on a phone the list and Save wrap
          below the sentence instead of squeezing it to a word per line. */}
      <span className="min-w-[16rem] flex-1">
        {mismatch ? (
          <>
            <span className="font-semibold">Your store sells in {s.currency}, but this workspace shows {data.currency}.</span>{' '}
            Order amounts arrive in {s.currency}, so every one is labelled {data.currency} here. Switch to {s.currency}: nothing is converted, only the label changes.
          </>
        ) : (
          <>
            <span className="font-semibold">Confirm your currency.</span>{' '}
            {s.source && s.source !== 'default'
              ? <>We suggest {s.currency}: {s.reason}.</>
              : <>Nothing tells us yet which currency you work in.</>}{' '}
            Every amount is shown in it and lead values are sent to ad platforms in it. Nothing is converted.
          </>
        )}
      </span>
      <span className="flex items-center gap-2">
        <CurrencySelect className="h-8 w-[200px] bg-card" value={staged.value} onChange={staged.setDraft}
          offered={data.offered} suggested={s.source !== 'default' ? s.currency : null} disabled={staged.saving} />
        <Button size="sm" className="h-8" onClick={staged.save} disabled={staged.saving || !staged.value}>
          {staged.saving && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
          Save
        </Button>
      </span>
    </div>
  );
}

/**
 * Settings > General > Currency: the choice, where it came from, and orders
 * that arrive in a currency other than the chosen one.
 */
export function CurrencySettingsCard() {
  const isAdmin = String(getUserRole() || '').toLowerCase() === 'admin';
  const { data, isLoading, isError } = useWorkspaceCurrency();
  const staged = useStagedChoice(data, { allowDefault: true });
  const other = data?.orders?.other || [];
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
          <Coins className="size-4 text-primary" />
          <h3 className="text-[15px] font-semibold">Currency</h3>
        </div>
      <CardContent className="space-y-4 p-5">
        {isLoading ? (
          <p className="text-[13px] text-muted-foreground">Loading...</p>
        ) : isError || !data ? (
          <p className="text-[13px] text-muted-foreground">Could not read the workspace currency. Reload to try again.</p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="ws-currency">Your workspace works in</Label>
              <div className="flex flex-wrap items-center gap-2">
                <CurrencySelect id="ws-currency" className="w-[240px]" value={staged.value} onChange={staged.setDraft}
                  offered={data.offered} suggested={data.suggestion?.source !== 'default' ? data.suggestion?.currency : null} disabled={!isAdmin || staged.saving} />
                {isAdmin && (staged.dirty || !data.confirmed) && (
                  <>
                    <Button size="sm" onClick={staged.save} disabled={staged.saving || !staged.value}>
                      {staged.saving && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
                      {data.confirmed ? 'Save' : 'Confirm'}
                    </Button>
                    {staged.dirty && (
                      <Button size="sm" variant="ghost" onClick={() => staged.setDraft(null)} disabled={staged.saving}>Cancel</Button>
                    )}
                  </>
                )}
              </div>
              <p className="text-[13px] text-muted-foreground">
                {data.confirmed
                  ? <>{SOURCE_WORDS.confirmed}{data.confirmed_at ? ` on ${fmtZoned(data.confirmed_at, 'd MMM yyyy')}` : ''}.</>
                  : <>{SOURCE_WORDS[data.source] || 'Detected'}: {data.suggestion?.reason}. Amounts show in {data.currency} until you confirm.</>}
                {data.confirmed && data.suggestion && data.suggestion.currency !== data.currency && data.suggestion.source !== 'default' && (
                  <> Detection would suggest {data.suggestion.currency} ({data.suggestion.reason}).</>
                )}
              </p>
              {!isAdmin && <p className="text-[13px] text-muted-foreground">Only a workspace admin can change it.</p>}
            </div>
            {staged.dirty && staged.value !== data.currency && (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[13px] leading-relaxed text-foreground">
                {relabelWords(data.currency, staged.value)}
              </p>
            )}
            {other.length > 0 && (
              <div className="rounded-md border card-attention px-3 py-2 text-[13px] leading-relaxed text-amber-900 dark:text-amber-200"
                data-testid="currency-other-orders">
                <span className="font-semibold">
                  {n(data.orders.other_count)} of your {n(data.orders.total)} orders in the last 180 days were in {other.map((o) => o.currency).join(', ')}, not {data.currency}.
                </span>{' '}
                Each order keeps its own currency on the order screen and is never converted. Shopify orders are stored and totalled in the store&rsquo;s currency; ad platforms get what the shopper paid.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The "currency" box a destination card has for a lead conversion's value.
 * Once the workspace has confirmed its currency, a lead's value is sent in it
 * (services/workspaceCurrency.leadValueCurrency) and this box only shows so;
 * until then the card's own setting is what leaves, as it always was.
 */
export function LeadValueCurrencyField({ value, onChange, className, title = 'Currency' }) {
  const { data } = useOrgSettings();
  const confirmed = data?.display?.currency_confirmed === true;
  const ws = data?.display?.currency || null;
  if (confirmed && ws) {
    return (
      <Input value={ws} disabled className={className}
        title={`Follows your workspace currency (${ws}, Settings > General). Lead values are sent in it.`} />
    );
  }
  return (
    <Input value={value || ''} title={title} placeholder={ws || 'INR'} className={className} spellCheck={false}
      onChange={(e) => onChange(e.target.value.toUpperCase().slice(0, 3))} />
  );
}
